import React from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import styles from './ProposalRejectionByProviderModal.module.scss';
import { Button, ButtonVariant } from 'v2/common/Button';
import {
  ExtraProposalRejectionOption,
  useProposalRejectionSelectOptions,
} from './hooks/useProposalRejectionSelectOptions';
import { useSubmitProposalRejection } from './hooks';
import { ProposalEntity } from 'v2/models';

const selectStyles = {
  control: {
    width: '100%',
    borderColor: 'var(--light-gray)',
  },
  dropdownIndicator: {
    right: '10px',
    justifyContent: 'flex-end',
  },
};

type ProposalRejectionByProviderModalProps = {
  proposal: ProposalEntity;
  show: boolean;
  onClose: () => void;
};

type ProposalRejectionByProviderForm = {
  proposalRejectionOptionID: ExtraProposalRejectionOption.other | number | null;
  reason: string;
};

export function ProposalRejectionByProviderModal({
  show,
  onClose,
  proposal,
}: ProposalRejectionByProviderModalProps) {
  const { control, resetField, handleSubmit, formState } =
    useForm<ProposalRejectionByProviderForm>({
      defaultValues: {
        reason: '',
        proposalRejectionOptionID: null,
      },
    });

  const {
    data: proposalRejectionSelectOptions,
    isLoading: isLoadingProposalRejectionSelectOptions,
  } = useProposalRejectionSelectOptions();

  const proposalRejectionOptionID = useWatch({
    control,
    name: 'proposalRejectionOptionID',
  });

  const submitProposalRejection = useSubmitProposalRejection(proposal.id);
  const onSubmit = handleSubmit(submitProposalRejection);
  const canSubmit = formState.isValid && !formState.isSubmitting;
  const dataBuyerName = proposal.data_buyer.name ?? 'Anonymous';

  return (
    <Modal
      title="Buyer Rejection"
      className={styles.modal}
      show={show}
      onHide={onClose}
      onExited={onClose}
      centered
      animation
    >
      <Modal.Header>
        <Modal.Title className="pl-3">Buyer Rejection</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={onSubmit}>
          <p className="text-light4">
            Please let us know why the request from {dataBuyerName} isn’t
            applicable to you.
          </p>

          <div className="mb-5">
            <Controller
              control={control}
              name="proposalRejectionOptionID"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <GeneralSelect
                  options={proposalRejectionSelectOptions}
                  handleChange={(option) => {
                    field.onChange(option?.value);

                    if (option?.value !== null) {
                      resetField('reason');
                    }
                  }}
                  placeHolder="Select an Option"
                  disabled={isLoadingProposalRejectionSelectOptions}
                  customStyles={selectStyles}
                />
              )}
            />

            {proposalRejectionOptionID ===
              ExtraProposalRejectionOption.other && (
              <Controller
                control={control}
                name="reason"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    className="mt-3"
                    placeholder={
                      "Please enter a reason why the provider wasn't a fit for this request"
                    }
                    aria-label="reason"
                    value={field.value}
                    onChange={(event) =>
                      field.onChange(event.currentTarget.value)
                    }
                    autoComplete="off"
                  />
                )}
              />
            )}
          </div>

          <div className="d-flex justify-content-end">
            <Button
              className="mr-3"
              variant={ButtonVariant.secondary_light}
              onClick={onClose}
            >
              CANCEL
            </Button>

            <Button
              variant={ButtonVariant.primary}
              type="submit"
              disabled={!canSubmit}
            >
              SUBMIT
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
